import { useState } from 'react';

import { shortenPaths } from './ToolOutputCard';

export interface GhostTraceProps {
  /** Past-tense verb describing the action (e.g. "ran", "read", "edited") */
  verb: string;
  /** Short description of the tool call (e.g. "server.ts", "Install dependencies") */
  description?: string;
  /** Tool input text for expanded detail */
  input?: string;
  /** Tool output text for expanded detail */
  output?: string;
  /** Tool is still running */
  loading?: boolean;
  /** Tool completed successfully */
  success?: boolean;
  /** Tool failed */
  error?: boolean;
}

function StatusDot({ loading, success, error }: { loading: boolean; success: boolean; error: boolean }) {
  if (loading) {
    return <span role="img" className="w-1 h-1 rounded-full bg-sd-warning/50 shrink-0 sd-breathe" aria-label="Running" />;
  }
  if (error) {
    return <span role="img" className="w-1 h-1 rounded-full bg-sd-error/50 shrink-0" aria-label="Error" />;
  }
  if (success) {
    return <span role="img" className="w-1 h-1 rounded-full bg-sd-success/50 shrink-0" aria-label="Success" />;
  }
  return <span role="img" className="w-1 h-1 rounded-full bg-sd-text-muted/30 shrink-0" aria-label="Pending" />;
}

export function GhostTrace({ verb, description, input, output, loading, success, error }: GhostTraceProps) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = !!(input || output);
  const isExpandable = !loading && hasDetail;

  const toggle = () => {
    if (isExpandable) setExpanded((prev) => !prev);
  };

  return (
    <div data-testid="ghost-trace" className="sd-fade-in">
      {/* Single whisper line */}
      <div
        className={`ml-10 pl-4 py-0.5 flex items-center gap-2 group/trace${isExpandable ? ' cursor-pointer' : ''}`}
        {...(isExpandable
          ? {
              role: 'button' as const,
              'aria-expanded': expanded,
              'aria-label': `${verb}${description ? ` ${description}` : ''}`,
              tabIndex: 0,
              onClick: toggle,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle();
                }
              },
            }
          : {})}
      >
        <StatusDot loading={!!loading} success={!!success} error={!!error} />

        {/* Verb + description */}
        {loading ? (
          <span className="text-[12px] text-sd-text-muted/40 italic leading-snug">
            {verb}
            {description ? ` — ${description}` : ''}...
          </span>
        ) : (
          <>
            <span className="text-[12px] text-sd-text-muted/50 leading-snug shrink-0">{verb}</span>
            {description && (
              <>
                <span className="text-[12px] text-sd-text-muted/30 leading-snug shrink-0">—</span>
                <span className="text-[12px] text-sd-text-muted/70 leading-snug truncate min-w-0">{description}</span>
              </>
            )}
          </>
        )}

        {/* Expand affordance — hover only */}
        {isExpandable && !expanded && (
          <span className="text-[11px] text-sd-text-muted/30 opacity-0 group-hover/trace:opacity-100 transition-opacity duration-200 shrink-0 select-none">
            details
          </span>
        )}
      </div>

      {/* Expanded detail — thin left line, monospace */}
      {expanded && hasDetail && (
        <div
          data-testid="ghost-trace-detail"
          className="ml-[3.75rem] border-l border-sd-border/15 pl-3 py-1 sd-fade-in"
        >
          {input && (
            <pre className="text-[11px] font-mono text-sd-text-muted/40 leading-relaxed whitespace-pre-wrap break-words m-0">
              {shortenPaths(input)}
            </pre>
          )}
          {output && (
            <pre className="text-[11px] font-mono text-sd-text-muted/50 leading-relaxed whitespace-pre-wrap break-words m-0">
              {shortenPaths(output)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
